import axios from "axios";

const base = process.env.REACT_APP_BACK_URL || `${window.location.protocol}//${window.location.hostname}:5000`;

type AnalisisFactura = {
  facturaId: string;
  proveedor: string;
  periodo: string;
  vencimiento: string;
  consumoKwh: number;
  importeTotal: number;
  energiaActiva: number;
  cargosFijos: number;
  impuestos: number;
  potenciaContratada?: number;
  factorPotencia?: number;
  titular?: string;
  direccion?: string;
  recomendaciones: string[];
  riesgo: "bajo" | "medio" | "alto";
  createdAt: string;
  archivoNombre: string;
  archivoMimeType?: string;
  archivoDataUrl?: string;
  archivoUrl?: string;
  detalle?: {
    invoice?: any;
    points?: any;
    ocr?: any;
    storage?: any;
  };
};

const buildTips = (consumoKwh: number, energiaActiva: number, cargosFijos: number): { riesgo: AnalisisFactura["riesgo"]; recomendaciones: string[] } => {
  const recomendaciones: string[] = [];
  let riesgo: AnalisisFactura["riesgo"] = "bajo";

  if (consumoKwh > 1200) {
    riesgo = "alto";
    recomendaciones.push("El consumo mensual es alto: sugerimos auditoría energética con ingeniero especializado.");
  } else if (consumoKwh > 700) {
    riesgo = "medio";
    recomendaciones.push("Consumo intermedio-alto: conviene revisar picos horarios y eficiencia de equipos críticos.");
  } else {
    recomendaciones.push("Consumo dentro de parámetros moderados: mantener controles y hábitos de eficiencia.");
  }

  if (cargosFijos > energiaActiva * 0.5) {
    recomendaciones.push("Los cargos fijos pesan mucho en la factura: revisar potencia contratada y encuadre tarifario.");
  }

  recomendaciones.push("Guardar históricos mensuales para respaldar reclamos por variaciones anómalas.");

  return { riesgo, recomendaciones };
};

const localKey = (socioId: string | number) => `energia_facturas_${socioId}`;

const fileToDataUrl = async (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const parseTitularDireccion = (data: any): { titular?: string; direccion?: string } => {
  const invoice = data?.invoice || {};
  const titular =
    invoice?.customerName ||
    invoice?.holderName ||
    invoice?.titular ||
    invoice?.clientName ||
    invoice?.name ||
    undefined;

  const direccion =
    invoice?.customerAddress ||
    invoice?.address ||
    invoice?.direccion ||
    undefined;

  if (titular || direccion) return { titular, direccion };

  const text = String(data?.rawText || data?.text || data?.fullText || "");
  if (!text.trim()) return {};

  const titularMatch = text.match(/(?:titular|cliente)\s*[:-]\s*([^\n\r]+)/i);
  const direccionMatch = text.match(/(?:direccion|domicilio)\s*[:-]\s*([^\n\r]+)/i);

  return {
    titular: titularMatch?.[1]?.trim() || undefined,
    direccion: direccionMatch?.[1]?.trim() || undefined,
  };
};

const saveLocal = (socioId: string | number, record: AnalisisFactura) => {
  const key = localKey(socioId);
  const prev = JSON.parse(localStorage.getItem(key) || "[]") as AnalisisFactura[];
  localStorage.setItem(key, JSON.stringify([record, ...prev]));
};

const removeLocal = (socioId: string | number, facturaId: string) => {
  const key = localKey(socioId);
  const prev = JSON.parse(localStorage.getItem(key) || "[]") as AnalisisFactura[];
  localStorage.setItem(
    key,
    JSON.stringify(prev.filter((r) => String(r.facturaId) !== String(facturaId)))
  );
};

const mockAnalisis = async (file: File, socioId: string | number): Promise<AnalisisFactura> => {
  const seed = Math.max(1, Math.round(file.size / 1024));
  const archivoDataUrl = await fileToDataUrl(file);
  const consumoKwh = 350 + (seed % 1400);
  const energiaActiva = consumoKwh * 230;
  const cargosFijos = Math.round(energiaActiva * (0.22 + (seed % 11) / 100));
  const impuestos = Math.round((energiaActiva + cargosFijos) * 0.21);
  const total = energiaActiva + cargosFijos + impuestos;
  const { riesgo, recomendaciones } = buildTips(consumoKwh, energiaActiva, cargosFijos);

  const record: AnalisisFactura = {
    facturaId: `FAC-${Date.now()}`,
    proveedor: "Distribuidora eléctrica",
    periodo: new Date().toLocaleDateString("es-AR", { month: "2-digit", year: "numeric" }),
    vencimiento: new Date(Date.now() + 1000 * 60 * 60 * 24 * 10).toLocaleDateString("es-AR"),
    consumoKwh,
    importeTotal: total,
    energiaActiva,
    cargosFijos,
    impuestos,
    potenciaContratada: 12.5,
    factorPotencia: 0.92,
    titular: "Titular no detectado",
    direccion: "Dirección no detectada",
    recomendaciones,
    riesgo,
    createdAt: new Date().toISOString(),
    archivoNombre: file.name,
    archivoMimeType: file.type || "application/octet-stream",
    archivoDataUrl,
    detalle: {
      invoice: {
        holderName: "Titular no detectado",
        address: "Dirección no detectada",
        totalAmount: total,
      },
      points: { awarded: Math.floor(total), rule: "1 punto = $1" },
      ocr: { source: "mock" },
    },
  };

  saveLocal(socioId, record);
  return record;
};

const analizarFactura = async (
  file: File,
  socioId: string | number,
  getAccessTokenSilently: () => Promise<string>
): Promise<AnalisisFactura> => {
  try {
    const token = await getAccessTokenSilently();
    const formData = new FormData();
    formData.append("image", file);
    formData.append("socio_id", String(socioId));

    const { data } = await axios.post(`${base}/invoice-ocr/scan`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });

    const amount = Number(data?.invoice?.totalAmount || 0);
    const consumoKwh = amount > 0 ? Math.round(amount / 180) : 0;
    const archivoDataUrl = await fileToDataUrl(file);
    const { titular, direccion } = parseTitularDireccion(data);
    const energiaActiva = Math.round(amount * 0.62);
    const cargosFijos = Math.round(amount * 0.2);
    const impuestos = Math.max(0, amount - energiaActiva - cargosFijos);
    const { riesgo, recomendaciones } = buildTips(consumoKwh, energiaActiva, cargosFijos);

    const record: AnalisisFactura = {
      facturaId: `FAC-${Date.now()}`,
      proveedor: "Distribuidora eléctrica",
      periodo: new Date().toLocaleDateString("es-AR", { month: "2-digit", year: "numeric" }),
      vencimiento: data?.invoice?.dueDate || "No detectado",
      consumoKwh,
      importeTotal: amount,
      energiaActiva,
      cargosFijos,
      impuestos,
      potenciaContratada: undefined,
      factorPotencia: undefined,
      titular,
      direccion,
      recomendaciones,
      riesgo,
      createdAt: new Date().toISOString(),
      archivoNombre: file.name,
      archivoMimeType: file.type || "application/octet-stream",
      archivoDataUrl,
      archivoUrl: data?.invoice?.fileUrl || data?.file_url || data?.storage?.imageUrl || undefined,
      detalle: {
        invoice: data?.invoice,
        points: data?.points,
        ocr: data?.ocr,
        storage: data?.storage,
      },
    };

    saveLocal(socioId, record);
    return record;
  } catch (error) {
    console.warn("No se pudo usar endpoint OCR real. Se usa análisis local estimado.", error);
    return mockAnalisis(file, socioId);
  }
};

const obtenerHistorial = async (
  socioId: string | number,
  getAccessTokenSilently: () => Promise<string>
): Promise<AnalisisFactura[]> => {
  try {
    const token = await getAccessTokenSilently();
    const { data } = await axios.get(`${base}/energia/facturas?socio_id=${socioId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data as AnalisisFactura[];
  } catch {
    return JSON.parse(localStorage.getItem(localKey(socioId)) || "[]") as AnalisisFactura[];
  }
};

const eliminarFactura = async (
  socioId: string | number,
  facturaId: string,
  getAccessTokenSilently: () => Promise<string>
): Promise<void> => {
  try {
    const token = await getAccessTokenSilently();
    await axios.delete(`${base}/energia/facturas/${facturaId}?socio_id=${socioId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    removeLocal(socioId, facturaId);
  }
};

const energiaAsistenciaService = {
  analizarFactura,
  obtenerHistorial,
  eliminarFactura,
};

export type { AnalisisFactura };
export default energiaAsistenciaService;
